import {
  endOfDay,
  endOfMonth,
  getMonth,
  getYear,
  isAfter,
  isEqual,
  isWithinInterval,
  startOfDay,
  addDays,
} from "date-fns";

import { useQSMStore } from "../../app/search-results/qsm-store/context";
import Calendar from "./calendar";
import React, { useState, useEffect } from "react";
import { isNil, set } from "lodash";
import { nlBE } from "date-fns/locale";
import { setFlexibleRange } from "../../app/search-results/qsm-store/reducer";
import { Trans } from "gatsby-plugin-react-i18next";

interface DateRangePickerProps {
  fromDate?: Date;
  toDate?: Date;
  focusMonth?: { month: number; year: number };
  locale?: Locale;
  duration?: number;
  flexsearch: boolean;
  disabledDaysFunction?: (date: Date) => boolean;
  extraClassNamesFunction?: (date: Date) => string[];
  dayContentFunction?: (date: Date) => JSX.Element | null | undefined;
  onSelectionChange?: (fromDate: Date, toDate: Date) => void;
  onFromDateChange?: (date?: Date) => void;
  onToDateChange?: (date?: Date) => void;
  onFocusMonthChange?: (focusMonth: { month: number; year: number }) => void;
}

const isDayDisabled = (date: Date) => {
  const today = startOfDay(new Date());
  return date >= today && date <= addDays(today, 3);
};

const DateRangePicker: React.FC<DateRangePickerProps> = (props) => {
  const [qsmState, qsmDispatch] = useQSMStore();
  const [fromDate, setFromDate] = useState<Date | undefined>(props.fromDate);
  const [toDate, setToDate] = useState<Date | undefined>(props.toDate);
  const [focusMonth, setFocusMonth] = useState<{ month: number; year: number }>(
    props.focusMonth ?? { year: getYear(new Date()), month: getMonth(new Date()) }
  );
  const [isWaitingForToDate, setWaitingForToDate] = useState<boolean>(false);
  const [flexibleRangeDatePicker, setFlexibleRangeDatePicker] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  const handleDayClick = (day: Date) => {
    const { onSelectionChange } = props;

    if (isWaitingForToDate && !isNil(fromDate) && isAfter(day, fromDate)) {
      setToDate(day);
      setWaitingForToDate(false);
      setFlexibleRangeDatePicker(null);

      if (props.onToDateChange) {
        props.onToDateChange(undefined);
      }

      if (onSelectionChange) {
        onSelectionChange(fromDate, day);
      }
    } else {
      setFromDate(day);
      setFlexibleRangeDatePicker(null);

      if (props.duration) {
        const to = new Date(
          Date.UTC(day.getFullYear(), day.getMonth(), day.getDate() + props.duration)
        );
        setToDate(to);

        if (onSelectionChange) {
          onSelectionChange(day, to);
        }
      } else {
        setToDate(undefined);
        setWaitingForToDate(true);
      }

      if (props.onFromDateChange) {
        props.onFromDateChange(day);
      }
    }
  };

  const handleDayMouseOver = (day: Date) => {
    if (
      isWaitingForToDate &&
      (isNil(fromDate) || isEqual(day, fromDate) || isAfter(day, fromDate))
    ) {
      setToDate(day);
    }
  };

  const handlePreviousClick = () => {
    const { month, year } = focusMonth;

    const previousMonth = (month - 1) % 12;
    const previousMonthsYear = previousMonth > month ? year - 1 : year;

    const newFocusMonth = { year: previousMonthsYear, month: previousMonth };

    setFocusMonth(newFocusMonth);

    if (props.onFocusMonthChange) {
      props.onFocusMonthChange(newFocusMonth);
    }
  };

  const handleNextClick = () => {
    const { month, year } = focusMonth;

    const nextMonth = (month + 1) % 12;
    const nextMonthsYear = nextMonth < month ? year + 1 : year;

    const newFocusMonth = { year: nextMonthsYear, month: nextMonth };

    setFocusMonth(newFocusMonth);

    if (props.onFocusMonthChange) {
      props.onFocusMonthChange(newFocusMonth);
    }
  };

  const today = startOfDay(new Date());

  const firstCalendarYear = focusMonth.year;
  const firstCalendarMonth = focusMonth.month;

  const secondCalendarMonth = (firstCalendarMonth + 1) % 12;
  const secondCalendarYear =
    secondCalendarMonth < firstCalendarMonth ? firstCalendarYear + 1 : firstCalendarYear;

  const checkIfDateIsSelected = (date: Date) => {
    const isDisabled = isDayDisabled(date);
    return isDisabled
      ? false
      : isNil(toDate)
      ? !isNil(fromDate) && isEqual(date, fromDate)
      : !isNil(fromDate) &&
        isWithinInterval(date, { start: startOfDay(fromDate), end: endOfDay(toDate) });
  };

  const checkIfDateIsInFlexibleRange = (date: Date) =>
    flexibleRangeDatePicker &&
    !isWithinInterval(date, { start: startOfDay(fromDate!), end: endOfDay(toDate!) }) &&
    isWithinInterval(date, {
      start: startOfDay(flexibleRangeDatePicker.start),
      end: endOfDay(flexibleRangeDatePicker.end),
    });

  useEffect(() => {
    setFromDate(props.fromDate);
    setToDate(props.toDate);

    if (fromDate && toDate) {
      const days = qsmState?.flexibleRange ?? 0;
      let newFromDate = new Date(fromDate);
      let newToDate = new Date(toDate);

      // Calculate the number of days between the start date and the disabled days
      let adjustedDays = days;
      for (let i = 1; i <= days; i++) {
        const checkDate = new Date(fromDate);
        checkDate.setDate(checkDate.getDate() - i);
        if (isDayDisabled(checkDate)) {
          adjustedDays--;
        }
      }

      newFromDate.setDate(newFromDate.getDate() - adjustedDays);
      newToDate.setDate(newToDate.getDate() + days);

      setFlexibleRangeDatePicker({ start: newFromDate, end: newToDate });

      if (props.onSelectionChange) {
        props.onSelectionChange(fromDate, toDate);
      }

      if (qsmDispatch) {
        qsmDispatch(setFlexibleRange(days));
      }
    }
  }, [props.fromDate?.valueOf(), props.toDate?.valueOf(), qsmState?.flexibleRange]);

  useEffect(() => {
    if (props.fromDate) {
      setFocusMonth({ month: props.fromDate.getMonth(), year: props.fromDate.getFullYear() });
    }
  }, [props.fromDate?.valueOf()]);

  useEffect(() => {
    if (!qsmState?.flexSearch) {
      setFlexibleRangeDatePicker(null);
      setFlexibleRange(0);
    }
  }, [qsmState?.flexSearch]);

  const handleFlexSearchClick = (days: number) => {
    if (qsmDispatch) {
      qsmDispatch(setFlexibleRange(days));
    }
  };

  return (
    <div className="date-range-picker-container">
      <div className="date-range-picker">
        <div className="date-range-picker__from">
          <Calendar
            year={firstCalendarYear}
            month={firstCalendarMonth}
            onDayClick={handleDayClick}
            onDayMouseOver={handleDayMouseOver}
            onPreviousClick={handlePreviousClick}
            hasPreviousButton={isAfter(
              new Date(firstCalendarYear, firstCalendarMonth),
              endOfMonth(today)
            )}
            hasNextButton={false}
            selectedDaysFunction={checkIfDateIsSelected}
            disabledDaysFunction={(date) =>
              isDayDisabled(date) ||
              (props.disabledDaysFunction && props.disabledDaysFunction(date))
            }
            extraClassNamesFunction={(date) => {
              const classes = props.extraClassNamesFunction
                ? props.extraClassNamesFunction(date)
                : [];
              if (checkIfDateIsInFlexibleRange(date)) {
                classes.push("date-range-picker__flexible-range");
              }
              return classes;
            }}
            dayContentFunction={props.dayContentFunction}
            locale={props.locale ?? nlBE}
            hasFixedHeight={true}
          />
        </div>
        <div className="date-range-picker__to">
          <Calendar
            year={secondCalendarYear}
            month={secondCalendarMonth}
            onDayClick={handleDayClick}
            onDayMouseOver={handleDayMouseOver}
            onNextClick={handleNextClick}
            hasPreviousButton={false}
            selectedDaysFunction={checkIfDateIsSelected}
            disabledDaysFunction={(date) =>
              isDayDisabled(date) ||
              (props.disabledDaysFunction && props.disabledDaysFunction(date))
            }
            extraClassNamesFunction={(date) => {
              const classes = props.extraClassNamesFunction
                ? props.extraClassNamesFunction(date)
                : [];
              if (checkIfDateIsInFlexibleRange(date)) {
                classes.push("date-range-picker__flexible-range");
              }
              return classes;
            }}
            dayContentFunction={props.dayContentFunction}
            locale={props.locale ?? nlBE}
            hasFixedHeight={true}
          />
        </div>
      </div>
      {/* if flexsearch is enabled */}

      {qsmState?.flexSearch && (
        <div className="date-range-picker-flex-days">
          <span
            className={` date-range-picker-flex-days__tag ${
              qsmState.flexibleRange === 0 ? "date-range-picker-flex-days__tag--active" : ""
            }`}
            onClick={() => handleFlexSearchClick(0)}
          >
            <Trans>EXACT_DATE</Trans>
          </span>
          <span
            className={`date-range-picker-flex-days__tag ${
              qsmState.flexibleRange === 1 ? "date-range-picker-flex-days__tag--active" : ""
            }`}
            onClick={() => handleFlexSearchClick(1)}
          >
            &#xB1; 1 <Trans>DAY</Trans>
          </span>
          <span
            className={`date-range-picker-flex-days__tag ${
              qsmState.flexibleRange === 2 ? "date-range-picker-flex-days__tag--active" : ""
            }`}
            onClick={() => handleFlexSearchClick(2)}
          >
            &#xB1; 2 <Trans>DAYS</Trans>
          </span>
          <span
            className={`date-range-picker-flex-days__tag ${
              qsmState.flexibleRange === 3 ? "date-range-picker-flex-days__tag--active" : ""
            }`}
            onClick={() => handleFlexSearchClick(3)}
          >
            &#xB1; 3 <Trans>DAYS</Trans>
          </span>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
